// EcosystemMarketplace — Browse and search ecosystem profiles + directory collaborators
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Globe, Search, Star, CheckCircle, X, SlidersHorizontal, MapPin, GraduationCap, Building2, Banknote, Users } from 'lucide-react'
import type { EcosystemProfile, EcosystemRole, EcosystemSearchFilters, ReputationTier, DirectoryEntity, DirectoryFacets, Project } from '../../types'
import { searchEcosystemProfiles, browseDirectory, searchDirectory, getDirectoryFacets, getProjects, searchEcosystemByProject } from '../../services/api'
import { useNavigate, useSearchParams } from 'react-router-dom'
import CollaboratorDetailModal from '../../components/marketplace/CollaboratorDetailModal'
import { disambiguateProjectLabels } from '../../utils/projects'
import PageHeader from '../../components/ui/PageHeader'
import EmptyState from '../../components/ui/EmptyState'
import ProjectRelevanceBadge from '../../components/ecosystem/ProjectRelevanceBadge'
import ConnectModal from '../../components/ecosystem/ConnectModal'
import MyConnectionsPanel from '../../components/ecosystem/MyConnectionsPanel'
import HowMatchingWorks from '../../components/ecosystem/HowMatchingWorks'
import { computeProjectRelevance } from '../../components/ecosystem/domainOverlap'

type SourceFilter = 'all' | 'ecosystem' | 'directory'

const ALL_ROLES: { value: EcosystemRole; label: string; color: string }[] = [
  { value: 'inventor', label: 'Inventor', color: 'bg-blue-100 text-blue-700' },
  { value: 'reviewer', label: 'Reviewer', color: 'bg-green-100 text-green-700' },
  { value: 'manufacturer', label: 'Manufacturer', color: 'bg-orange-100 text-orange-700' },
  { value: 'investor', label: 'Investor', color: 'bg-amber-100 text-amber-700' },
  { value: 'advisor', label: 'Advisor', color: 'bg-purple-100 text-purple-700' },
  { value: 'researcher', label: 'Researcher', color: 'bg-teal-100 text-teal-700' },
]

const ROLE_COLOR_MAP: Record<string, string> = Object.fromEntries(
  ALL_ROLES.map((r) => [r.value, r.color])
)

const TIER_COLORS: Record<ReputationTier, string> = {
  bronze: 'bg-amber-700/10 text-amber-800',
  silver: 'bg-gray-200 text-gray-700',
  gold: 'bg-yellow-100 text-yellow-700',
  platinum: 'bg-indigo-100 text-indigo-700',
}

const TIER_OPTIONS: ReputationTier[] = ['bronze', 'silver', 'gold', 'platinum']

const AVAILABILITY_OPTIONS = [
  { value: '', label: 'Any' },
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'project_based', label: 'Project-based' },
  { value: 'advisory', label: 'Advisory' },
] as const

const DOMAIN_OPTIONS = [
  'Biotech', 'Clean Energy', 'Electronics', 'Materials Science',
  'Medical Devices', 'Pharmaceuticals', 'Robotics', 'Software',
  'Sustainability', 'Telecommunications',
]

const PAGE_SIZE = 12

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${i <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
        />
      ))}
      <span className="text-xs text-gray-600 ml-0.5">{rating.toFixed(1)}</span>
    </div>
  )
}

function SkeletonCard() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-12 h-12 rounded-full bg-gray-200" />
        <div className="flex-1">
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-2" />
          <div className="h-3 bg-gray-100 rounded w-1/3" />
        </div>
      </div>
      <div className="h-3 bg-gray-100 rounded w-full mb-2" />
      <div className="h-3 bg-gray-100 rounded w-4/5 mb-3" />
      <div className="flex gap-1.5 mb-3">
        <div className="h-5 bg-gray-100 rounded w-16" />
        <div className="h-5 bg-gray-100 rounded w-20" />
        <div className="h-5 bg-gray-100 rounded w-14" />
      </div>
      <div className="h-8 bg-gray-100 rounded w-full" />
    </div>
  )
}

const DIRECTORY_ENTITY_TYPE_VALUES = new Set(['researcher', 'institution', 'corporate', 'funding_body'])

export default function EcosystemMarketplace() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [profiles, setProfiles] = useState<EcosystemProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all')
  const [directoryEntities, setDirectoryEntities] = useState<DirectoryEntity[]>([])
  const [directoryLoading, setDirectoryLoading] = useState(false)

  // Directory filter state
  const [selectedEntityTypes, setSelectedEntityTypes] = useState<Set<string>>(new Set())
  const [selectedCountry, setSelectedCountry] = useState('')
  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set())
  const [facets, setFacets] = useState<DirectoryFacets | null>(null)
  const [directoryTotal, setDirectoryTotal] = useState(0)
  const [directoryLoadingMore, setDirectoryLoadingMore] = useState(false)
  // BUG-093: track the directory page in a ref, NOT a state dep of fetchDirectory.
  // When it lived in the dep array, clicking "Load More" (which bumps the page)
  // recreated the callback → the mount effect re-ran fetchDirectory() with
  // append=false and snapped the list straight back to page 1.
  const directoryPageRef = useRef(1)
  // BUG-093: the ecosystem `/ecosystem/search` endpoint has no offset/cursor, so
  // "Load More" is implemented as a growing display window — each click widens the
  // requested limit and re-fetches, then REPLACES the list. The old code re-ran
  // the identical page-1 query and appended it, duplicating page 1 forever.
  const displayCountRef = useRef(PAGE_SIZE)

  // Alphabetical filter
  const [selectedLetters, setSelectedLetters] = useState<Set<string>>(new Set())

  // Project filter state
  const [projects, setProjects] = useState<Project[]>([])
  const [selectedProjectId, setSelectedProjectId] = useState('')
  const [, setProjectSynthesis] = useState('')
  const [projectLoading, setProjectLoading] = useState(false)

  // Detail modal state
  const [detailEntity, setDetailEntity] = useState<DirectoryEntity | null>(null)

  // Connect modal + My Connections panel (project-domain-overlap redesign)
  const [connectProfile, setConnectProfile] = useState<EcosystemProfile | null>(null)
  const [showMyConnections, setShowMyConnections] = useState(false)

  // Real match signal from /ecosystem/search-by-project: the backend returns
  // a genuine `total` for the project-context result set, and each match
  // carries a real `cosine_sim` embedding-similarity score. Neither is
  // modelled on the EcosystemProfile type (they're project-context-only
  // fields on the raw response), so they're captured separately here rather
  // than fabricated.
  const [projectMatchTotal, setProjectMatchTotal] = useState<number | null>(null)
  const [projectCosineByProfile, setProjectCosineByProfile] = useState<Record<string, number>>({})

  // Filter state
  const [selectedRoles, setSelectedRoles] = useState<Set<EcosystemRole>>(new Set())
  const [selectedDomains, setSelectedDomains] = useState<Set<string>>(new Set())
  const [minRating, setMinRating] = useState<number>(0)
  const [verifiedOnly, setVerifiedOnly] = useState(false)
  const [selectedTier, setSelectedTier] = useState<ReputationTier | ''>('')
  const [selectedAvailability, setSelectedAvailability] = useState('')

  // Fetch projects on mount
  useEffect(() => {
    getProjects().then(setProjects).catch(() => {})
  }, [])

  // Story #5305 Stream 14b (Task #5318): "Find funding bodies" hands off
  // here from the Overlap fund-once workbench with `?type=funding_body` —
  // seed the directory filter ONCE on mount so the handoff actually
  // pre-filters instead of landing on an unfiltered browse (this component
  // has no other URL-param sync, so this reads once and never fights a
  // later manual filter change). An unrecognised/missing `type` is silently
  // ignored, same convention AggregatorPortfolio.tsx's own `view` param
  // read uses.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const requestedType = searchParams.get('type')
    if (requestedType && DIRECTORY_ENTITY_TYPE_VALUES.has(requestedType)) {
      setSourceFilter('directory')
      setSelectedEntityTypes(new Set([requestedType]))
    }
  }, [])

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(timer)
  }, [query])

  // Fetch directory facets when needed
  useEffect(() => {
    if (sourceFilter === 'ecosystem' || facets) return
    getDirectoryFacets().then(setFacets).catch(() => {})
  }, [sourceFilter, facets])

  const buildFilters = useCallback((): EcosystemSearchFilters => {
    const filters: EcosystemSearchFilters = {}
    if (selectedRoles.size === 1) filters.role = [...selectedRoles][0]
    if (selectedDomains.size === 1) filters.domain = [...selectedDomains][0]
    if (minRating > 0) filters.min_rating = minRating
    if (verifiedOnly) filters.verified_only = true
    if (selectedTier) filters.tier = selectedTier as ReputationTier
    if (selectedAvailability) filters.availability = selectedAvailability as EcosystemSearchFilters['availability']
    return filters
  }, [selectedRoles, selectedDomains, minRating, verifiedOnly, selectedTier, selectedAvailability])

  const fetchProfiles = useCallback(async (append = false) => {
    if (append) { setLoadingMore(true); displayCountRef.current += PAGE_SIZE }
    else { setLoading(true); displayCountRef.current = PAGE_SIZE }

    // BUG-093: grow the requested window on each "Load More" and always REPLACE
    // (not append) with the freshly-fetched, sliced set — the backend has no
    // offset, so appending a second identical fetch duplicated page 1.
    const displayCount = displayCountRef.current

    try {
      const limit = displayCount + 1

      // Project-context filter mode (Story #2232)
      if (selectedProjectId) {
        setProjectLoading(true)
        try {
          const result = await searchEcosystemByProject(selectedProjectId, buildFilters(), PAGE_SIZE)
          const allMatches = result.matches || []
          // Split unified results: ecosystem profiles vs directory entities
          let ecoMatches = allMatches.filter((m) => (m as unknown as Record<string, string>).source === 'ecosystem_profile')
          const dirMatches = allMatches.filter((m) => (m as unknown as Record<string, string>).source === 'collaborator_directory') as unknown as DirectoryEntity[]
          // Client-side multi-select filtering (API only supports single-value filters)
          if (selectedRoles.size > 0) {
            ecoMatches = ecoMatches.filter((p) => selectedRoles.has(p.primary_role))
          }
          if (selectedDomains.size > 0) {
            // BUG-024: guard `domains` — missing arrays must not crash the page.
            ecoMatches = ecoMatches.filter((p) => p.domains?.some((d) => selectedDomains.has(d)) ?? false)
          }
          setProfiles(ecoMatches)
          // Directory entities are researchers — show them when no role filter is active
          // or when Researcher role is explicitly selected; hide for other role selections
          const showDirectory = sourceFilter !== 'ecosystem' && (selectedRoles.size === 0 || selectedRoles.has('researcher'))
          setDirectoryEntities(showDirectory ? dirMatches : [])
          setProjectSynthesis(result.project_synthesis || '')
          setHasMore(false)

          // Real match count + real per-profile embedding similarity — both
          // come straight off the search-by-project response (`total` and
          // `cosine_sim`), never fabricated. EcosystemProfile doesn't model
          // these project-context-only fields, so read them defensively.
          setProjectMatchTotal(typeof result.total === 'number' ? result.total : allMatches.length)
          const cosineMap: Record<string, number> = {}
          ecoMatches.forEach((m) => {
            const raw = (m as unknown as Record<string, number>).cosine_sim
            if (typeof raw === 'number' && Number.isFinite(raw)) {
              cosineMap[m.profile_id] = raw
            }
          })
          setProjectCosineByProfile(cosineMap)
        } catch {
          setProfiles([])
          setDirectoryEntities([])
          setProjectSynthesis('')
          setProjectMatchTotal(null)
          setProjectCosineByProfile({})
        } finally {
          setProjectLoading(false)
          setLoading(false)
          setLoadingMore(false)
          setDirectoryLoading(false)
        }
        return
      }

      // Not in project-context mode — clear any stale project-match data
      // (real total / real cosine similarity) from a previous selection.
      setProjectMatchTotal(null)
      setProjectCosineByProfile({})

      if (sourceFilter === 'directory') {
        // Directory only — skip ecosystem profiles
        setProfiles([])
        setHasMore(false)
      } else if (sourceFilter === 'all' && debouncedQuery) {
        // Merged search — fire both APIs in parallel
        const dirParams = {
          query: debouncedQuery,
          entity_type: selectedEntityTypes.size > 0 ? [...selectedEntityTypes] : undefined,
          country_code: selectedCountry || undefined,
          topics: selectedTopics.size > 0 ? [...selectedTopics] : undefined,
          mode: 'hybrid' as const,
          size: PAGE_SIZE,
        }
        const [ecoResult, dirResult] = await Promise.allSettled([
          searchEcosystemProfiles(debouncedQuery, buildFilters(), limit),
          searchDirectory(dirParams),
        ])

        // Ecosystem profiles
        if (ecoResult.status === 'fulfilled') {
          let filtered = ecoResult.value
          if (selectedRoles.size > 1) filtered = filtered.filter((p) => selectedRoles.has(p.primary_role))
          // BUG-024: guard `domains` — a profile without a domains array must not
          // crash the whole marketplace into the error boundary.
          if (selectedDomains.size > 0) filtered = filtered.filter((p) => p.domains?.some((d) => selectedDomains.has(d)) ?? false)
          const hasMoreResults = filtered.length > displayCount
          setProfiles(hasMoreResults ? filtered.slice(0, displayCount) : filtered)
          setHasMore(hasMoreResults)
        } else {
          setProfiles([])
          setHasMore(false)
        }

        // Directory entities
        if (dirResult.status === 'fulfilled') {
          setDirectoryEntities(dirResult.value.entities || [])
        } else {
          setDirectoryEntities([])
        }
        setDirectoryLoading(false)
      } else {
        const results = await searchEcosystemProfiles(
          debouncedQuery || undefined,
          buildFilters(),
          limit
        )

        // Client-side filtering for multi-select fields not supported by API single-value filters
        let filtered = results
        if (selectedRoles.size > 1) {
          filtered = filtered.filter((p) => selectedRoles.has(p.primary_role))
        }
        if (selectedDomains.size > 0) {
          // BUG-024: guard `domains` — missing arrays must not crash the page.
          filtered = filtered.filter((p) =>
            p.domains?.some((d) => selectedDomains.has(d)) ?? false
          )
        }

        // BUG-093: `displayCount` grows on each Load More; always REPLACE the list
        // with the freshly-fetched, sliced window (the previous append re-added the
        // same page-1 results because the backend has no offset).
        const hasMoreResults = filtered.length > displayCount
        const page = hasMoreResults ? filtered.slice(0, displayCount) : filtered
        setProfiles(page)
        setHasMore(hasMoreResults)
      }
    } catch {
      if (!append) setProfiles([])
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [debouncedQuery, buildFilters, selectedRoles, selectedDomains, sourceFilter, selectedEntityTypes, selectedCountry, selectedTopics, selectedProjectId, selectedTier, verifiedOnly])

  const DIRECTORY_PAGE_SIZE = 100

  // Fetch directory entities when sourceFilter includes directory
  const fetchDirectory = useCallback(async (append = false) => {
    if (sourceFilter === 'ecosystem') {
      setDirectoryEntities([])
      setDirectoryTotal(0)
      return
    }
    if (append) setDirectoryLoadingMore(true)
    else { setDirectoryLoading(true); directoryPageRef.current = 1 }

    // BUG-093: read/advance the page from the ref so this callback does NOT depend
    // on directory-page state (which caused the Load-More→reset-to-1 loop).
    const page = append ? directoryPageRef.current + 1 : 1
    try {
      const params = {
        query: debouncedQuery || undefined,
        entity_type: selectedEntityTypes.size > 0 ? [...selectedEntityTypes] : undefined,
        country_code: selectedCountry || undefined,
        topics: selectedTopics.size > 0 ? [...selectedTopics] : undefined,
        size: DIRECTORY_PAGE_SIZE,
        page,
        page_size: DIRECTORY_PAGE_SIZE,
      }
      const fetcher = debouncedQuery ? searchDirectory({ ...params, mode: 'hybrid' as const }) : browseDirectory(params)
      const result = await fetcher
      const newEntities = result.entities || []
      if (append) {
        setDirectoryEntities((prev) => [...prev, ...newEntities])
      } else {
        setDirectoryEntities(newEntities)
      }
      setDirectoryTotal(result.total || 0)
      directoryPageRef.current = page
    } catch {
      if (!append) setDirectoryEntities([])
    } finally {
      setDirectoryLoading(false)
      setDirectoryLoadingMore(false)
    }
  }, [debouncedQuery, sourceFilter, selectedEntityTypes, selectedCountry, selectedTopics])

  useEffect(() => {
    fetchDirectory()
  }, [fetchDirectory])

  useEffect(() => {
    fetchProfiles()
  }, [fetchProfiles])

  const clearFilters = () => {
    setSelectedRoles(new Set())
    setSelectedDomains(new Set())
    setMinRating(0)
    setVerifiedOnly(false)
    setSelectedTier('')
    setSelectedAvailability('')
    setSelectedEntityTypes(new Set())
    setSelectedCountry('')
    setSelectedTopics(new Set())
    setSelectedLetters(new Set())
    setSourceFilter('all')
    setQuery('')
  }

  const toggleRole = (role: EcosystemRole) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev)
      if (next.has(role)) next.delete(role)
      else next.add(role)
      return next
    })
  }

  const toggleDomain = (domain: string) => {
    setSelectedDomains((prev) => {
      const next = new Set(prev)
      if (next.has(domain)) next.delete(domain)
      else next.add(domain)
      return next
    })
  }

  const toggleEntityType = (et: string) => {
    setSelectedEntityTypes((prev) => {
      const next = new Set(prev)
      if (next.has(et)) next.delete(et)
      else next.add(et)
      return next
    })
  }

  const toggleTopic = (topic: string) => {
    setSelectedTopics((prev) => {
      const next = new Set(prev)
      if (next.has(topic)) next.delete(topic)
      else next.add(topic)
      return next
    })
  }

  const toggleLetter = (letter: string) => {
    setSelectedLetters((prev) => {
      const next = new Set(prev)
      if (next.has(letter)) next.delete(letter)
      else next.add(letter)
      return next
    })
  }

  // Extract surname (last word of name) for sorting/filtering
  const getSurname = (name: string) => {
    const parts = (name || '').trim().split(/\s+/)
    return parts[parts.length - 1] || ''
  }

  // Filter by surname initial + sort alphabetically by surname
  const filteredDirectoryEntities = useMemo(() => {
    let filtered = directoryEntities
    if (selectedLetters.size > 0) {
      filtered = filtered.filter((e) => {
        const surnameInitial = getSurname(e.canonical_name).charAt(0).toUpperCase()
        return selectedLetters.has(surnameInitial)
      })
    }
    return [...filtered].sort((a, b) =>
      getSurname(a.canonical_name).localeCompare(getSurname(b.canonical_name))
    )
  }, [directoryEntities, selectedLetters])

  // Live "N partners match" count — derived from real result data, never a
  // fabricated total. `approx` marks a lower bound (there ARE more results,
  // we just don't have an exact server-side total for them) vs an exact figure.
  const totalMatchCount = useMemo(() => {
    if (selectedProjectId && projectMatchTotal != null) {
      return { value: projectMatchTotal, approx: false }
    }
    if (sourceFilter === 'directory') {
      return { value: directoryTotal, approx: false }
    }
    if (sourceFilter === 'ecosystem') {
      return { value: profiles.length, approx: hasMore }
    }
    return { value: profiles.length + directoryTotal, approx: hasMore }
  }, [selectedProjectId, projectMatchTotal, sourceFilter, directoryTotal, profiles.length, hasMore])

  const matchCountReady = selectedProjectId
    ? !projectLoading
    : sourceFilter === 'directory'
      ? !directoryLoading
      : sourceFilter === 'ecosystem'
        ? !loading
        : !loading && !directoryLoading

  const hasActiveFilters =
    selectedRoles.size > 0 ||
    selectedDomains.size > 0 ||
    minRating > 0 ||
    verifiedOnly ||
    selectedTier !== '' ||
    selectedAvailability !== '' ||
    selectedEntityTypes.size > 0 ||
    selectedCountry !== '' ||
    selectedTopics.size > 0 ||
    selectedLetters.size > 0

  // Alun's build v2 review: the page opened straight onto the unfiltered "all"
  // result set, with the filter panel — the more useful starting point on a
  // network this size — easy to overlook beside it. hasEngaged is true the
  // moment the reader has actually asked for something (a filter, a search
  // term, or a project to match against); until then the results grid stays
  // off, on purpose, so the filters are the thing the eye lands on. The data
  // fetch itself is untouched — this only changes what gets rendered, so the
  // facet counts next to each filter are still real and still there the
  // instant a filter is touched.
  const hasEngaged = hasActiveFilters || debouncedQuery.trim() !== '' || selectedProjectId !== ''

  const ENTITY_TYPE_OPTIONS = [
    { value: 'researcher', label: 'Researcher' },
    { value: 'institution', label: 'Institution' },
    { value: 'corporate', label: 'Corporate' },
    { value: 'funding_body', label: 'Funding Body' },
  ]

  const directoryFilterPanel = (
    <div className="space-y-5">
      {/* Entity Type filter */}
      <div>
        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Entity Type</h4>
        <div className="space-y-1.5">
          {ENTITY_TYPE_OPTIONS.map((et) => {
            const count = facets?.entity_types?.find((f) => f.value === et.value)?.count
            return (
              <label key={et.value} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={selectedEntityTypes.has(et.value)}
                  onChange={() => toggleEntityType(et.value)}
                  className="rounded border-gray-300 text-iflow-500 focus:ring-iflow-500"
                />
                {et.label}
                {count != null && <span className="text-[10px] text-gray-500">({count})</span>}
              </label>
            )
          })}
        </div>
      </div>

      {/* Country filter */}
      <div>
        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Country</h4>
        <select
          value={selectedCountry}
          onChange={(e) => setSelectedCountry(e.target.value)}
          className="w-full rounded-lg border border-gray-300 text-sm px-3 py-2 focus:ring-iflow-500 focus:border-iflow-500"
        >
          <option value="">Any country</option>
          {(facets?.countries || []).slice(0, 30).map((c) => (
            <option key={c.value} value={c.value}>
              {c.value} ({c.count})
            </option>
          ))}
        </select>
      </div>

      {/* Topics filter */}
      {facets?.topics && facets.topics.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Topics</h4>
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {facets.topics.slice(0, 10).map((t) => (
              <label key={t.value} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={selectedTopics.has(t.value)}
                  onChange={() => toggleTopic(t.value)}
                  className="rounded border-gray-300 text-iflow-500 focus:ring-iflow-500"
                />
                <span className="truncate">{t.value}</span>
                <span className="text-[10px] text-gray-500 shrink-0">({t.count})</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Clear filters */}
      {hasActiveFilters && (
        <button
          onClick={clearFilters}
          className="w-full text-sm text-iflow-600 hover:text-iflow-700 font-medium py-2 border border-sky-200 rounded-lg hover:bg-iflow-50 transition-colors"
        >
          Clear filters
        </button>
      )}
    </div>
  )

  const filterPanel = (
    <div className="space-y-5">
      {/* Project filter (Story #2232) */}
      {projects.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Filter by Project</h4>
          <select
            value={selectedProjectId}
            onChange={(e) => { setSelectedProjectId(e.target.value); setProjectSynthesis('') }}
            className="w-full rounded-lg border border-gray-300 bg-white text-gray-900 text-sm px-3 py-2 focus:ring-sky-500 focus:border-sky-500 appearance-auto"
          >
            <option value="">All (no filter)</option>
            {disambiguateProjectLabels(projects.filter(p => p.status === 'active')).map(p => (
              <option key={p.project_id} value={p.project_id}>{p.displayName} ({p.document_count} docs)</option>
            ))}
          </select>
          {projectLoading && (
            <p className="text-xs text-gray-500 animate-pulse mt-1">Analysing project context...</p>
          )}
        </div>
      )}

      {/* Role filter */}
      <div>
        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Role</h4>
        <div className="space-y-1.5">
          {ALL_ROLES.map((r) => (
            <label key={r.value} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
              <input
                type="checkbox"
                checked={selectedRoles.has(r.value)}
                onChange={() => toggleRole(r.value)}
                className="rounded border-gray-300 text-sky-500 focus:ring-sky-500"
              />
              {r.label}
            </label>
          ))}
        </div>
      </div>

      {/* Domain filter */}
      <div>
        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Domain</h4>
        <div className="space-y-1.5 max-h-40 overflow-y-auto">
          {DOMAIN_OPTIONS.map((d) => (
            <label key={d} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
              <input
                type="checkbox"
                checked={selectedDomains.has(d)}
                onChange={() => toggleDomain(d)}
                className="rounded border-gray-300 text-sky-500 focus:ring-sky-500"
              />
              {d}
            </label>
          ))}
        </div>
      </div>

      {/* Min rating */}
      <div>
        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Min Rating</h4>
        <select
          value={minRating}
          onChange={(e) => setMinRating(Number(e.target.value))}
          className="w-full rounded-lg border border-gray-300 text-sm px-3 py-2 focus:ring-sky-500 focus:border-sky-500"
        >
          <option value={0}>Any rating</option>
          <option value={1}>1+ stars</option>
          <option value={2}>2+ stars</option>
          <option value={3}>3+ stars</option>
          <option value={4}>4+ stars</option>
          <option value={5}>5 stars</option>
        </select>
      </div>

      {/* Verified only */}
      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <div
            role="switch"
            aria-checked={verifiedOnly}
            onClick={() => setVerifiedOnly(!verifiedOnly)}
            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
              verifiedOnly ? 'bg-sky-500' : 'bg-gray-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                verifiedOnly ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </div>
          <span className="text-sm text-gray-700">Verified only</span>
        </label>
      </div>

      {/* Reputation tier */}
      <div>
        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Reputation Tier</h4>
        <select
          value={selectedTier}
          onChange={(e) => setSelectedTier(e.target.value as ReputationTier | '')}
          className="w-full rounded-lg border border-gray-300 text-sm px-3 py-2 focus:ring-sky-500 focus:border-sky-500"
        >
          <option value="">Any tier</option>
          {TIER_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Availability */}
      <div>
        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Availability</h4>
        <select
          value={selectedAvailability}
          onChange={(e) => setSelectedAvailability(e.target.value)}
          className="w-full rounded-lg border border-gray-300 text-sm px-3 py-2 focus:ring-sky-500 focus:border-sky-500"
        >
          {AVAILABILITY_OPTIONS.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label}
            </option>
          ))}
        </select>
      </div>

      {/* Clear filters */}
      {hasActiveFilters && (
        <button
          onClick={clearFilters}
          className="w-full text-sm text-sky-600 hover:text-sky-700 font-medium py-2 border border-sky-200 rounded-lg hover:bg-sky-50 transition-colors"
        >
          Clear filters
        </button>
      )}
    </div>
  )

  return (
    <div className="max-w-7xl mx-auto">
      <PageHeader
        title="Ecosystem"
        description="Discover organisations and experts you could collaborate with, then connect and link them to a project."
        helpKey="ecosystem"
        secondaryActions={
          <button
            onClick={() => setShowMyConnections(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-iflow-200 px-4 py-2 text-sm font-medium text-iflow-700 hover:bg-iflow-50 transition-colors"
          >
            <Users className="w-4 h-4" /> My connections
          </button>
        }
      />

      <div className="mb-6">
        <HowMatchingWorks />
      </div>

      {/* Search bar */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search profiles by name, skill, or domain..."
          className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 shadow-sm"
        />
        {query && (
          <button
            onClick={() => setQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Source toggle tabs + live match count */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          {([
            { value: 'all', label: 'All' },
            { value: 'ecosystem', label: 'Ecosystem Profiles' },
            { value: 'directory', label: 'Collaborator Directory' },
          ] as const).map((tab) => (
            <button
              key={tab.value}
              onClick={() => setSourceFilter(tab.value)}
              className={`px-4 py-1.5 text-xs font-medium rounded-md transition-colors ${
                sourceFilter === tab.value
                  ? 'bg-white text-sky-600 shadow-sm border-b-2 border-sky-500'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {matchCountReady && (
          <p className="text-sm text-gray-500" data-testid="ecosystem-match-count">
            <span className="font-semibold text-gray-900">
              {totalMatchCount.value.toLocaleString()}
              {totalMatchCount.approx ? '+' : ''}
            </span>{' '}
            {totalMatchCount.value === 1 ? 'partner matches' : 'partners match'}
          </p>
        )}
      </div>

      {/* Mobile filter toggle */}
      <div className="lg:hidden mb-4">
        <button
          onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
          className="flex items-center gap-2 text-sm font-medium text-gray-700 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filters
          {hasActiveFilters && (
            <span className="w-5 h-5 rounded-full bg-sky-500 text-white text-xs flex items-center justify-center">
              !
            </span>
          )}
        </button>
      </div>

      <div className="flex gap-6">
        {/* Desktop filter sidebar */}
        <aside className="hidden lg:block w-60 shrink-0">
          <div className="bg-white rounded-xl border border-gray-200 p-4 sticky top-4">
            {sourceFilter === 'directory' ? (
              <>
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Directory Filters</h3>
                {directoryFilterPanel}
              </>
            ) : sourceFilter === 'ecosystem' ? (
              <>
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Profile Filters</h3>
                {filterPanel}
              </>
            ) : (
              <>
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Profile Filters</h3>
                {filterPanel}
                <div className="border-t border-gray-200 my-4" />
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Directory Filters</h3>
                {directoryFilterPanel}
              </>
            )}
          </div>
        </aside>

        {/* Mobile filter overlay */}
        {mobileFiltersOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/30" onClick={() => setMobileFiltersOpen(false)} />
            <div className="absolute right-0 top-0 h-full w-72 bg-white shadow-xl p-5 overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Filters</h3>
                <button onClick={() => setMobileFiltersOpen(false)} className="text-gray-500 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              {sourceFilter === 'directory' ? directoryFilterPanel : sourceFilter === 'ecosystem' ? filterPanel : (
                <>
                  {filterPanel}
                  <div className="border-t border-gray-200 my-4" />
                  <h4 className="text-sm font-semibold text-gray-900 mb-4">Directory Filters</h4>
                  {directoryFilterPanel}
                </>
              )}
            </div>
          </div>
        )}

        {/* Results area */}
        <div className="flex-1 min-w-0">
          {!hasEngaged ? (
            /* Less is more (Alun, build v2 review): land on the filters, not
               on every profile and collaborator the network has. */
            <EmptyState
              icon={SlidersHorizontal}
              title="Choose a filter or search to see who's here"
              description="Use the filters on the left, or search by name, domain or role, to find inventors, reviewers, manufacturers, funders and more."
            />
          ) : loading || projectLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : profiles.length === 0 && directoryEntities.length === 0 ? (
            /* Empty state — guidance, not a blank screen (P4) */
            <EmptyState
              icon={Globe}
              title={sourceFilter === 'directory'
                ? 'No collaborators have been ingested yet'
                : 'No ecosystem profiles found'}
              description={hasActiveFilters
                ? 'Try adjusting your filters or search query.'
                : sourceFilter === 'directory'
                  ? 'Directory collaborators will appear here once ingested from OpenAlex, ROR, or PatentsView.'
                  : 'Be the first to register your profile so collaborators can find you.'}
              action={hasActiveFilters ? (
                <button
                  onClick={clearFilters}
                  className="px-5 py-2.5 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800 transition-colors"
                >
                  Clear filters
                </button>
              ) : sourceFilter !== 'directory' ? (
                <button
                  onClick={() => navigate('/settings')}
                  className="px-5 py-2.5 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800 transition-colors"
                >
                  Create your profile
                </button>
              ) : undefined}
            />
          ) : (
            <>
              {/* Ecosystem Profiles section */}
              {sourceFilter !== 'directory' && profiles.length > 0 && (
                <>
                  {sourceFilter === 'all' && directoryEntities.length > 0 && (
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Ecosystem Profiles</h3>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {profiles.map((profile) => {
                      // Real, live project-domain-overlap — never a fabricated
                      // relevance number (computeProjectRelevance returns
                      // count: 0 whenever no genuine overlap can be found).
                      const relevance = computeProjectRelevance(profile.domains, projects)
                      // Real embedding-similarity from /ecosystem/search-by-project
                      // (cosine_sim), only present in project-context mode.
                      const rawCosine = projectCosineByProfile[profile.profile_id]
                      const matchPercent =
                        typeof rawCosine === 'number' ? Math.max(0, Math.min(1, rawCosine)) * 100 : undefined
                      return (
                      <div
                        key={profile.profile_id}
                        className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow"
                      >
                        {/* Avatar + name + role */}
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center shrink-0 overflow-hidden">
                            {profile.avatar_url ? (
                              <img src={profile.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                            ) : (
                              <span className="text-sm font-semibold text-gray-500">
                                {getInitials(profile.name)}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-bold text-gray-900 truncate">{profile.name}</span>
                              {profile.verified && (
                                <CheckCircle className="w-3.5 h-3.5 text-sky-500 shrink-0" />
                              )}
                            </div>
                            <span
                              className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-medium mt-0.5 ${
                                ROLE_COLOR_MAP[profile.primary_role] || 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {profile.primary_role}
                            </span>
                            {selectedProjectId && (
                              <span className="inline-block text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full ml-1 mt-0.5">Matched via project context</span>
                            )}
                          </div>
                        </div>

                        {/* Headline */}
                        <p className="text-xs text-gray-500 mb-3 line-clamp-2">{profile.headline}</p>

                        {/* Top 3 skills */}
                        {/* BUG-024: guard `skills` — a profile missing the array must not crash the card. */}
                        {profile.skills && profile.skills.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {profile.skills.slice(0, 3).map((skill) => (
                              <span
                                key={skill}
                                className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full"
                              >
                                {skill}
                              </span>
                            ))}
                            {profile.skills.length > 3 && (
                              <span className="text-[10px] text-gray-500">+{profile.skills.length - 3}</span>
                            )}
                          </div>
                        )}

                        {/* Real, live project-domain-overlap indicator (Fig47) — the
                            PROMINENT per-card signal, shown first/above the rating so
                            it's the primary thing a user reads, not an afterthought.
                            Omitted entirely when there's no genuine overlap to show
                            (never a fabricated relevance number). */}
                        <ProjectRelevanceBadge
                          relevantCount={relevance.count}
                          sharedDomains={relevance.sharedDomains}
                          matchPercent={matchPercent}
                        />

                        {/* Rating + tier — real, but secondary metadata beneath the
                            relevance signal */}
                        <div className="flex items-center justify-between mb-3">
                          <StarRating rating={profile.avg_rating} />
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full font-medium capitalize ${
                              TIER_COLORS[profile.reputation_tier] || TIER_COLORS.bronze
                            }`}
                          >
                            {profile.reputation_tier}
                          </span>
                        </div>

                        {/* View Profile + Connect actions */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/ecosystem/${profile.profile_id}`)}
                            className="flex-1 text-sm font-medium text-sky-600 py-2 border border-sky-200 rounded-lg hover:bg-sky-50 transition-colors"
                          >
                            View Profile
                          </button>
                          <button
                            onClick={() => setConnectProfile(profile)}
                            className="flex-1 text-sm font-medium text-white bg-iflow-700 py-2 rounded-lg hover:bg-iflow-800 transition-colors"
                          >
                            Connect
                          </button>
                        </div>
                      </div>
                      )
                    })}
                  </div>

                  {/* Load more ecosystem profiles */}
                  {hasMore && (
                    <div className="flex justify-center mt-6">
                      <button
                        onClick={() => fetchProfiles(true)}
                        disabled={loadingMore}
                        className="px-6 py-2.5 text-sm font-medium text-sky-600 border border-sky-200 rounded-lg hover:bg-sky-50 transition-colors disabled:opacity-50"
                      >
                        {loadingMore ? 'Loading...' : 'Load More'}
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Section divider when showing both */}
              {sourceFilter === 'all' && profiles.length > 0 && directoryEntities.length > 0 && (
                <div className="border-t border-gray-200 my-6" />
              )}

              {/* Directory Collaborators section */}
              {sourceFilter !== 'ecosystem' && directoryEntities.length > 0 && (
                <>
                  {sourceFilter === 'all' && profiles.length > 0 && (
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Directory Collaborators</h3>
                  )}

                  {/* Alphabetical selector */}
                  <div className="flex flex-wrap gap-0.5 mb-4">
                    {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((letter) => (
                      <button
                        key={letter}
                        onClick={() => toggleLetter(letter)}
                        className={`w-8 h-8 text-xs font-medium rounded transition-colors ${
                          selectedLetters.has(letter)
                            ? 'bg-iflow-700 text-white'
                            : 'bg-white text-gray-600 border border-gray-200 hover:bg-iflow-50 hover:text-iflow-600'
                        }`}
                      >
                        {letter}
                      </button>
                    ))}
                    {selectedLetters.size > 0 && (
                      <button
                        onClick={() => setSelectedLetters(new Set())}
                        className="px-3 h-8 text-xs font-medium text-gray-500 hover:text-gray-700 border border-gray-200 rounded hover:bg-gray-50 ml-1"
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  <p className="text-sm text-gray-500 mb-3">
                    {filteredDirectoryEntities.length}
                    {directoryTotal > 0 && directoryTotal > directoryEntities.length
                      ? ` of ${directoryTotal.toLocaleString()}`
                      : ''} entities
                  </p>

                  {directoryLoading ? (
                    <div className="space-y-2">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={`dir-skel-${i}`} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
                          <div className="flex items-center gap-3">
                            <div className="w-5 h-5 bg-gray-200 rounded" />
                            <div className="h-4 bg-gray-200 rounded w-40" />
                            <div className="h-3 bg-gray-100 rounded w-20 ml-auto" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredDirectoryEntities.map((entity) => {
                        const TypeIcon = entity.entity_type === 'researcher' ? GraduationCap
                          : entity.entity_type === 'funding_body' ? Banknote
                          : entity.entity_type === 'institution' ? Building2
                          : Building2
                        return (
                          <div
                            key={entity.entity_id}
                            onClick={() => setDetailEntity(entity)}
                            className="bg-white rounded-lg border border-gray-200 p-4 hover:border-iflow-300 hover:shadow-sm transition-all cursor-pointer"
                          >
                            <div className="flex items-start gap-3">
                              <TypeIcon className="w-4 h-4 text-iflow-500 mt-0.5 shrink-0" />
                              <div className="min-w-0 flex-1">
                                <h3 className="text-sm font-semibold text-gray-900 line-clamp-1">
                                  {entity.canonical_name}
                                </h3>
                                {selectedProjectId && (
                                  <span className="text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full">Matched via project context</span>
                                )}
                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                  {entity.country_code && (
                                    <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                                      <MapPin className="w-2.5 h-2.5" />
                                      {entity.city ? `${entity.city}, ` : ''}{entity.country_code}
                                    </span>
                                  )}
                                  {entity.h_index != null && entity.h_index > 0 && (
                                    <span className="text-[10px] text-gray-500">h-index: {entity.h_index}</span>
                                  )}
                                </div>
                                {entity.topics && entity.topics.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {entity.topics.slice(0, 5).map((t) => (
                                      <span key={t} className="px-1.5 py-0.5 bg-sky-500/10 text-gray-600 text-[10px] rounded">
                                        {t}
                                      </span>
                                    ))}
                                    {entity.topics.length > 5 && (
                                      <span className="text-[10px] text-gray-500">+{entity.topics.length - 5}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <span className="px-2 py-0.5 text-[10px] font-medium bg-iflow-50 text-iflow-700 rounded-full capitalize shrink-0">
                                {entity.entity_type.replace('_', ' ')}
                              </span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {/* Load More directory entities */}
                  {directoryTotal > directoryEntities.length && (
                    <div className="flex justify-center mt-4">
                      <button
                        onClick={() => fetchDirectory(true)}
                        disabled={directoryLoadingMore}
                        className="px-6 py-2.5 text-sm font-medium text-iflow-600 border border-iflow-200 rounded-lg hover:bg-iflow-50 transition-colors disabled:opacity-50"
                      >
                        {directoryLoadingMore ? 'Loading...' : `Load More (${directoryEntities.length.toLocaleString()} of ${directoryTotal.toLocaleString()})`}
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Collaborator Detail Modal */}
      <CollaboratorDetailModal
        isOpen={detailEntity !== null}
        onClose={() => setDetailEntity(null)}
        entity={detailEntity}
      />

      {/* Connect modal — real, live connection request submission */}
      {connectProfile && (
        <ConnectModal profile={connectProfile} onClose={() => setConnectProfile(null)} />
      )}

      {/* My Connections panel — real getEcosystemConnections() data */}
      {showMyConnections && (
        <MyConnectionsPanel onClose={() => setShowMyConnections(false)} />
      )}
    </div>
  )
}
